import React, { Component } from "react";
import ReactGA from "react-ga";
import BannerImg from "../../assets/images/banner/legal.png";
import IndustryVerticalPage from "../../components/industry/industry-vertical-page";

const page = {
  title: "Legal Document Management System, Case Management & Law Firm Solutions | Avani Cimcon",
  description:
    "Legal software solutions for case management, document management, billing and data exchange. Modernize law firm systems safely and add AI-enabled document workflows.",
  keywords:
    "Law Practice Management Software, Document Management, Attorney Software, Case management, Legal Case Management Software, Law Office Management Software, Law Firm Financial Management Software, Law Firm Accounting Software, Legal Calendar Software, legal document management systems, document management systems law firms, Legal Solutions Suite, Document Management System, legal document systems, document management software, Legal Time and Billing Software, Data Exchange and Transaction Processing modules, enterprise software engineering, legacy modernization, AI enablement, case management system, law firm solution suite",
  canonical: "https://www.avanicimcon.com/legal",
  ogImage: "https://www.avanicimcon.com/images/banner/legal.png",
  hero: {
    eyebrow: "Industry Expertise",
    heading: "Solutions for Law Firms",
    lead:
      "Law firms and legal operations teams need dependable case management, document handling, billing and data exchange systems that can keep up with changing business needs.",
    image: BannerImg,
    tags: ["Case management", "Document management", "Law firm software", "Data exchange"],
    panelKicker: "Legacy systems. Modern enterprise delivery.",
    panelTitle: "Legal technology for case, document and billing workflows.",
    panelText:
      "We help law firms preserve existing case management systems while improving document handling, integrations and operational efficiency.",
    stats: [
      { value: "CMS", label: "Case management system heritage" },
      { value: "DMS", label: "Document management workflows" },
      { value: "SOA", label: "Data exchange and transaction processing" },
    ],
    primaryCta: { label: "Talk to Us", href: "/contact-us" },
    secondaryCta: { label: "See IT Consulting Services", href: "/it-consulting-services" },
  },
  overview: {
    eyebrow: "Industry Overview",
    title: "Legal workflows that connect case management, documents, billing and exchange",
    lead:
      "The legal page should preserve the long-lived terminology around law practice management software, document management and case management while presenting the page as an enterprise modernization story.",
    paragraphs: [
      "At Avani Cimcon Technologies, we have significant experience in the legal domain. Law firms face dynamically changing business needs, increased competition and costs, which creates demand for innovative methods to manage operations.",
      "We have been working with leading law firms in the US and providing solutions that effectively manage operations, while allowing the enterprise software engineering narrative to stay aligned with the approved homepage story.",
    ],
    callout: {
      title: "What this page should prove",
      text: "Legal buyers should see that we understand case workflows, document control, vendor integration and billing interfaces.",
      items: [
        "Case management and workflow automation",
        "Document management and repository structures",
        "Transaction processing and data exchange",
      ],
    },
  },
  challenges: {
    eyebrow: "Industry Challenges",
    title: "Legal operations need precision, auditability and tight vendor integration",
    items: [
      {
        title: "Complex case workflows",
        text: "Foreclosure, bankruptcy, eviction, litigation and monitor workflows require careful orchestration.",
      },
      {
        title: "Document and matter control",
        text: "Legal teams must manage documents by matter type and year while maintaining repository integrity and access controls.",
      },
      {
        title: "Third-party data exchange",
        text: "Law firms rely on many vendor systems and servicers, so data exchange must be stable and repeatable.",
      },
    ],
  },
  systems: {
    eyebrow: "Enterprise Systems We Build & Modernize",
    title: "Legal platforms that support cases, documents, transactions and billing",
    lead:
      "Preserve the existing product and technology keywords while positioning them as proof of enterprise delivery depth.",
    items: [
      {
        title: "Case Management System",
        text: "CMS modules for foreclosure, bankruptcy, eviction, litigation, monitor, reforeclosure and deed in lieu.",
        href: "/cms",
        linkLabel: "Explore CMS",
      },
      {
        title: "Document Management System",
        text: "Repository and workflow structures for legal documents across different matter types and years.",
        href: "/cms",
        linkLabel: "Explore CMS",
      },
      {
        title: "Data Exchange Modules",
        text: "SOA-based middleware for exchanging case data with vendor systems and servicers.",
        href: "/dxchange",
        linkLabel: "Explore DXchange",
      },
      {
        title: "Billing Interfaces",
        text: "Accounting and billing integrations that reduce redundant data entry and improve invoice processing.",
        href: "/it-consulting-services",
        linkLabel: "See solutions",
      },
    ],
  },
  modernization: {
    eyebrow: "Legacy Modernization Opportunities",
    title: "Modernize the legal stack by improving workflow, document and integration layers",
    lead:
      "This section should explain how law firms can preserve the case-management core while modernizing the surrounding application and integration landscape.",
    paragraphs: [
      "The legal solution suite includes data exchange and transaction processing modules, case management, document management and accounting interfaces. That combination makes it a strong candidate for incremental modernization rather than wholesale replacement.",
      "Our middleware solution helps law firms exchange case management data with vendors, servicers and trading partners in a seamless bi-directional, real-time manner. That is exactly the sort of operational improvement that helps protect legacy investment while improving productivity.",
    ],
    callout: {
      title: "Modernization priorities",
      items: [
        "SOA middleware for vendor and servicer integration",
        "Better document repository structures",
        "Automation of repetitive billing and processing steps",
        "Preservation of case workflow terminology and search intent",
      ],
    },
  },
  ai: {
    eyebrow: "AI Enablement Opportunities",
    title: "AI should support document-heavy legal workflows and case intelligence",
    lead:
      "AI works best in legal environments when it sits on top of the existing case and document systems.",
    items: [
      {
        title: "Case summarization",
        text: "Use AI to help legal teams summarize case histories, filings and milestones faster.",
      },
      {
        title: "Document intelligence",
        text: "Use AI to classify legal documents, extract clauses and route matter-specific content.",
      },
      {
        title: "Docket and deadline intelligence",
        text: "Use predictive workflows to support docket management and reduce missed steps.",
      },
    ],
  },
  experience: {
    eyebrow: "Our Experience in This Industry",
    title: "Law firm solution suite experience built on real operational systems",
    paragraphs: [
      "We have developed an array of solutions which we collectively call the law firm solution suite. This suite enables small to large law firms that do not have in-house IT to interact with many vendor systems and servicers directly or via integration with third-party products or middleware solutions.",
      "The system includes major modules such as case management, document management, data exchange and transaction processing, and accounting and billing interface. Those assets are exactly the kind of authority-rich content we should preserve and modernize rather than replace.",
    ],
    callout: {
      title: "Evidence to preserve",
      items: [
        "US law firm delivery history",
        "Case Management System modules",
        "Data exchange with NetDirector and others",
        "Billing and invoice processing integrations",
      ],
    },
  },
  relatedSolutions: [
    { label: "IT Consulting Services", href: "/it-consulting-services", text: "Enterprise modernization and integration." },
    { label: "CMS", href: "/cms", text: "Document and content platform heritage." },
    { label: "DXchange", href: "/dxchange", text: "Middleware for legal data exchange." },
    { label: "Value Proposition", href: "/value-proposition", text: "Why enterprises modernize with Avani Cimcon." },
  ],
  why: {
    eyebrow: "Why Avani Cimcon",
    title: "Legal engineering that respects workflow precision and long-term trust",
    lead:
      "The page should reinforce reliability, integration depth and evidence of long-term delivery in law firm environments.",
    items: [
      {
        title: "Operational understanding",
        text: "We know the workflows behind foreclosure, litigation, bankruptcy and related matter types.",
      },
      {
        title: "Data exchange expertise",
        text: "We can integrate with servicers, vendors and third-party legal systems through middleware.",
      },
      {
        title: "SEO preservation",
        text: "We keep the legal document management system, attorney software and case management keywords intact.",
      },
      {
        title: "Practical modernization",
        text: "We improve the systems around the legal core without weakening the legal story.",
      },
    ],
  },
  cta: {
    eyebrow: "Talk to Us",
    title: "Modernize legal workflows without disrupting the case management core",
    text:
      "Keep the systems that law firms trust and extend them with enterprise software engineering, middleware and AI support.",
    primary: { label: "Contact Us", href: "/contact-us" },
    secondary: { label: "Explore Enterprise AI", href: "/enterprise-ai" },
  },
};

class Legal extends Component {
  render() {
    ReactGA.initialize("UA-5235714-2");
    ReactGA.pageview(window.location.pathname + window.location.search);

    return <IndustryVerticalPage {...page} />;
  }
}

export default Legal;
